// Chat messages of phone v2 (F_Chat modes stuck / help, F_Phone), shared with the desktop chat:
//   <StuckMsg/>   kind 'stuck': her line + the reasons card (step 'ask': "STUCK ON: …", six reasons, "Or just tell me,
//                 in your own words.") or the help card (step 'help': FIRST STEP · 2 MIN / TWO MINUTES / TOGETHER /
//                 ROUGH VERSION · 2 MIN + Start, WHEN IS BETTER? chips, "too big" split cards + Use these, then
//                 "Something else"). Once acted on, the help collapses to her line + the outcome line.
//   <DoneNote/>   kind 'done': the tick line ("You ticked off “…” · +15 points") + her done line. Hidden after Undo.
//   <ChatPill/>   the centred glass line both use.
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import type { CharId, DoneNoteData, Message, StuckAction, StuckChatData, StuckReason } from '@shared/types';
import { STUCK_REASONS } from '@shared/content/lines';
import { STUCK_CARD, stuckAlt, stuckStep } from '@shared/content/stuck';
import { taskShort } from '@shared/rules';
import { stuckAction, stuckChat } from '../../store';
import { go } from '../../router';
import { Bubble, Icon, MessageRow, PrimaryButton, type IconName } from '../../components';
import s from './chatCards.module.css';

export function ChatPill({ icon, children }: { icon: IconName; children: ComponentChildren }) {
  return (
    <p class={s.pill}>
      <Icon name={icon} size={16} strokeWidth={2.6} color="var(--acc)" />
      <span>{children}</span>
    </p>
  );
}

/** The 'done' note. Returns null once undone (the app hides it). */
export function DoneNote({ m, ch }: { m: Message; ch: CharId }) {
  const d = m.data as DoneNoteData | undefined;
  if (!d || d.undone) return null;
  return (
    <div class={s.stack}>
      <ChatPill icon="check">{m.text}</ChatPill>
      {d.line && <Bubble from="buddy" ch={ch} face={false} e={2}>{d.line}</Bubble>}
    </div>
  );
}

/**
 * A stuck message. `current`: for an ask, whether its reasons card still shows (it is open, or its help is the open
 * one, so the picked reason stays lit above it). `onWait` is told while a reason is on its way (her help may take a
 * few seconds), so the chat can show her typing.
 */
export function StuckMsg({ m, ch, face, current, onWait }: {
  m: Message; ch: CharId; face: boolean; current: boolean; onWait?: (waiting: boolean) => void;
}) {
  const d = m.data as StuckChatData | undefined;
  const [busy, setBusy] = useState<string | null>(null);
  if (!d) return m.text ? <Bubble from="buddy" ch={ch} face={face} e={4}>{m.text}</Bubble> : null;
  const line = m.text ? <Bubble from="buddy" ch={ch} face={face} e={d.step === 'ask' ? 4 : 1}>{m.text}</Bubble> : null;
  const open = d.status === 'open';

  const act = async (key: string, a: StuckAction) => {
    if (busy) return;
    setBusy(key);
    const r = await stuckAction(m.id, a);
    setBusy(null);
    if (r && a.action === 'start' && d.help) go('W', { cur: r.cardId || d.cardId, alt: stuckAlt(d.help, d.title), from: 'C' });
  };

  if (d.step === 'ask') {
    if (!open && !current) return line;
    const pick = async (reason: StuckReason) => {
      if (!open || busy) return;
      setBusy(reason); onWait?.(true);
      await stuckChat(d.cardId, reason);
      setBusy(null); onWait?.(false);
    };
    return (
      <div class={s.stack}>
        {line}
        <div class={s.card} role="group" aria-label={`Stuck on: ${d.title}`}>
          <p class={s.head}>{`STUCK ON: ${taskShort(d.title).toUpperCase()}`}</p>
          <div class={s.grid2}>
            {STUCK_REASONS.map((r) => {
              const on = d.reason === r.id || busy === r.id;
              return (
                <button
                  key={r.id} type="button" class={s.reason} aria-pressed={on ? 'true' : 'false'}
                  data-dim={(d.reason || busy) && !on ? 'true' : 'false'} aria-disabled={!open || !!busy ? 'true' : undefined}
                  onClick={() => void pick(r.id)}
                >{r.label}</button>
              );
            })}
          </div>
          <p class={s.own}>Or just tell me, in your own words.</p>
        </div>
      </div>
    );
  }

  // step 'help'
  const h = d.help;
  if (!open || !h) {
    return (
      <div class={s.stack}>
        {line}
        {d.status === 'resolved' && d.out && <ChatPill icon="arrow">{d.out}</ChatPill>}
      </div>
    );
  }
  const split = h.reason === 'big' && h.split && h.split.length >= 2 ? h.split : null;
  const step = split ? null : stuckStep(h, d.title);
  return (
    <div class={s.stack}>
      {line}
      <div class={s.card} data-help="true" role="group" aria-label={`Help with: ${d.title}`}>
        <p class={s.head}>{split ? `SPLIT INTO ${split.length} SMALL CARDS` : STUCK_CARD[h.reason].head}</p>
        {step && <p class={s.title}>{step}</p>}
        {split && <div class={s.rows}>{split.map((x, i) => <MessageRow key={i} time={x.time} title={x.title} side={`${x.min} min`} />)}</div>}
        {h.reason === 'time' ? (
          <div class={s.grid2}>
            {h.chips.map((t) => (
              <button key={t} type="button" class={s.chip} aria-busy={busy === t ? 'true' : undefined} onClick={() => void act(t, { action: 'later', when: t })}>{t}</button>
            ))}
          </div>
        ) : split ? (
          <PrimaryButton h={48} busy={busy === 'split'} onClick={() => void act('split', { action: 'split' })}>{`Use these ${split.length} cards`}</PrimaryButton>
        ) : (
          <PrimaryButton h={48} icon="play" busy={busy === 'start'} onClick={() => void act('start', { action: 'start' })}>{STUCK_CARD[h.reason].btn}</PrimaryButton>
        )}
        <button type="button" class={s.other} onClick={() => void act('again', { action: 'again' })}>Something else</button>
      </div>
    </div>
  );
}
